import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { formatDate } from '../utils/helper';

interface ServiceHistoryTableProps {
  services: any[];
  onView: (vehicleId: string) => void;
  onEdit: (service: any, vehicleId: string) => void;
  onDelete: (serviceId: string, vehicleId: string) => void;
}

export default function ServiceHistoryTable({
  services,
  onView,
  onEdit,
  onDelete,
}: ServiceHistoryTableProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const calculateTotalCost = (service: any) => {
    // Use new fields if available
    if (service.totalServiceCost) {
      return service.totalServiceCost;
    }
    // Fallback to legacy field
    return service.amountOfParts || 0;
  };

  // Mobile card view
  if (isMobile) {
    return (
      <Box sx={{ width: '100%' }}>
        {services.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              No service history found
            </Typography>
          </Paper>
        ) : (
          services.map((service) => (
            <Paper
              key={`${service.vehicleId}-${service._id}`}
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#062141' }}>
                    {service.partDetails || service.serviceType || 'N/A'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {service.vehicleNumber || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="View Service">
                    <IconButton
                      size="small"
                      onClick={() => onView(service.vehicleId)}
                      sx={{ color: '#1976d2' }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Service">
                    <IconButton
                      size="small"
                      onClick={() => onEdit(service, service.vehicleId)}
                      sx={{ color: '#4caf50' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Service">
                    <IconButton
                      size="small"
                      onClick={() => onDelete(service._id, service.vehicleId)}
                      sx={{ color: '#d32f2f' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, fontSize: '0.875rem' }}>
                <Box>
                  <Typography variant="caption" color="textSecondary">Date</Typography>
                  <Typography variant="body2">{formatDate(service.spareChangedDate) || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Total Cost</Typography>
                  <Typography variant="body2">₹{calculateTotalCost(service).toFixed(2)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">ODO</Typography>
                  <Typography variant="body2">{service.odoReading || service.odo || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Mechanic</Typography>
                  <Typography variant="body2">{service.mechanicName || service.changedBy || 'N/A'}</Typography>
                </Box>
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Typography variant="caption" color="textSecondary">Description</Typography>
                  <Typography variant="body2">{service.description || service.remarks || 'N/A'}</Typography>
                </Box>
              </Box>
            </Paper>
          ))
        )}
      </Box>
    );
  }

  // Desktop table view
  return (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: 2,
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        overflowX: 'auto',
      }}
    >
      <Table sx={{ minWidth: 900 }} aria-label="service history table">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#062141' }}>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Service Type</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Vehicle No.</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>ODO</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Parts Cost</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Labour</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Total Cost</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Mechanic</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Service Center</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {services.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                <Typography variant="body1" color="textSecondary">
                  No service history found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            services.map((service) => (
              <TableRow
                key={`${service.vehicleId}-${service._id}`}
                sx={{
                  '&:nth-of-type(even)': { backgroundColor: '#f8f9fa' },
                  '&:hover': { backgroundColor: '#e3f2fd' },
                  '&:last-child td, &:last-child th': { border: 0 },
                }}
              >
                <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
                  {service.serviceType || service.partDetails || 'N/A'}
                </TableCell>
                <TableCell>{service.vehicleNumber || 'N/A'}</TableCell>
                <TableCell>{formatDate(service.spareChangedDate) || 'N/A'}</TableCell>
                <TableCell>{service.odoReading || 'N/A'}</TableCell>
                <TableCell>₹{(service.totalPartsCost || 0).toFixed(2)}</TableCell>
                <TableCell>₹{(service.labourCost || 0).toFixed(2)}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>₹{calculateTotalCost(service).toFixed(2)}</TableCell>
                <TableCell>{service.mechanicName || service.changedBy || 'N/A'}</TableCell>
                <TableCell>{service.serviceCenter || 'N/A'}</TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                    <Tooltip title="View Service">
                      <IconButton
                        size="small"
                        onClick={() => onView(service.vehicleId)}
                        sx={{ color: '#1976d2' }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Service">
                      <IconButton
                        size="small"
                        onClick={() => onEdit(service, service.vehicleId)}
                        sx={{ color: '#4caf50' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Service">
                      <IconButton
                        size="small"
                        onClick={() => onDelete(service._id, service.vehicleId)}
                        sx={{ color: '#d32f2f' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
